import React, { useState, useMemo } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"

/**
 * TableKPICard component for displaying data in a simple two-column table
 * with expandable functionality and row navigation
 *
 * @param {Object} props
 * @param {string} props.title - Title of the KPI card
 * @param {Array} props.data - Array of objects to display in table
 * @param {string} props.keyField - The field name to use for the left column
 * @param {string} props.valueField - The field name to use for the right column
 * @param {Function} props.keyRenderer - Optional custom renderer for key column
 * @param {Function} props.valueRenderer - Optional custom renderer for value column
 * @param {string} props.bgColor - Background color class
 * @param {string} props.textColor - Text color for values
 * @param {string} props.titleColor - Text color for the title
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {number} props.maxItems - Maximum number of items to show initially
 * @param {Function} props.rowClick - Click handler for rows
 * @param {boolean} props.isClickable - Whether rows are clickable
 */
const TableKPICard = ({
  title,
  data = [],
  keyField = "name",
  valueField = "value",
  keyRenderer,
  valueRenderer,
  bgColor = "bg-admin_paper",
  textColor = "text-admin_text",
  titleColor = "!text-admin_text_secondary",
  icon,
  maxItems = 5,
  rowClick,
  isClickable = false,
}) => {
  // State to track if the card is expanded
  const [isExpanded, setIsExpanded] = useState(false)

  // Default renderers
  const defaultKeyRenderer = (item) => item[keyField]
  const defaultValueRenderer = (item) => item[valueField]

  // Use provided renderers or defaults
  const renderKey = keyRenderer || defaultKeyRenderer
  const renderValue = valueRenderer || defaultValueRenderer

  // Handle row click
  const handleRowClick = (item, e) => {
    // Only process click if rowClick is provided and isClickable is true
    if (isClickable && rowClick) {
      e.stopPropagation() // Prevent event bubbling
      rowClick(item)
    }
  }

  // Sort data to prevent flickering when data order changes
  const sortedData = useMemo(() => {
    // Create a copy of the data to avoid mutating props
    return [...data].sort((a, b) => {
      // First sort by the valueField in DESCENDING order (higher values first)
      const valueA = a[valueField]
      const valueB = b[valueField]

      // Handle numeric values for proper numeric comparison (DESCENDING)
      if (typeof valueA === "number" && typeof valueB === "number") {
        if (valueA !== valueB) return valueB - valueA // Note the reversed order for descending
      } else {
        // String comparison for non-numeric values (DESCENDING)
        const valueAStr = String(valueA).toLowerCase()
        const valueBStr = String(valueB).toLowerCase()
        if (valueAStr < valueBStr) return 1 // Reversed for descending
        if (valueAStr > valueBStr) return -1 // Reversed for descending
      }

      // If values are the same, sort by keyField to ensure consistent order
      const keyA = a[keyField]
      const keyB = b[keyField]

      // Handle numeric keys
      if (typeof keyA === "number" && typeof keyB === "number") {
        return keyA - keyB
      } else {
        // String comparison for non-numeric values
        const keyAStr = String(keyA).toLowerCase()
        const keyBStr = String(keyB).toLowerCase()
        if (keyAStr < keyBStr) return -1
        if (keyAStr > keyBStr) return 1
      }

      return 0
    })
  }, [data, keyField, valueField])

  // Determine which items to display based on expanded state
  const displayData = isExpanded ? sortedData : sortedData.slice(0, maxItems)
  const hasMoreItems = !isExpanded && sortedData.length > maxItems

  // Toggle expanded state
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div
      className={twMerge(
        bgColor,
        "flex flex-col h-full rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
      )}
    >
      <div className="flex justify-between items-start mb-4">
        <Typography
          component="h2"
          variant="admin_h4"
          className="text-admin_text font-medium mb-4"
        >
          {title}
        </Typography>

        {icon && <div className="w-10 xl:w-14 h-10 xl:h-14 flex items-center justify-center bg-admin_canvas rounded-lg">{icon}</div>}
      </div>

      {/* Table content */}
      <div className="flex-grow">
        {displayData.length > 0 ? (
          <div className="space-y-6">
            {displayData.map((item, index) => (
              <div
                // Use a more stable key that combines the key value and index
                key={`${item[keyField]}-${item[valueField]}-${index}`}
                className={twMerge(
                  "flex justify-between items-center",
                  isClickable
                    ? "cursor-pointer hover:bg-admin_canvas p-2 rounded-md transition-colors"
                    : ""
                )}
                onClick={(e) => handleRowClick(item, e)}
                role={isClickable ? "button" : undefined}
                aria-label={
                  isClickable ? `Filter by ${renderKey(item)}` : undefined
                }
              >
                <Typography
                  variant="admin_body"
                  className={twMerge(textColor, "my-0")}
                >
                  {renderKey(item)}
                </Typography>

                <Typography
                  component="output"
                  variant="admin_body_accent"
                  className={twMerge(textColor, "my-0")}
                >
                  {renderValue(item)}
                </Typography>
              </div>
            ))}
          </div>
        ) : (
          <Typography variant="admin_body" className="text-admin_text_secondary">
            No data available
          </Typography>
        )}
      </div>

      {/* Show "more" button if data is truncated */}
      {sortedData.length > maxItems && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="admin_secondary"
            onClick={handleToggleExpand}
            className="flex items-center gap-2"
          >
            {isExpanded ? (
              <>
                Show Less <ChevronUpIcon className="w-4 h-4" />
              </>
            ) : (
              <>
                + {sortedData.length - maxItems} more{" "}
                <ChevronDownIcon className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      )}

      {/* Clickable hint */}
      {isClickable && displayData.length > 0 && (
        <Typography
          variant="admin_txt2"
          className="text-admin_text_secondary mt-4 text-xs italic text-center"
        >
          Click on a row to view filtered tickets
        </Typography>
      )}
    </div>
  )
}

export default TableKPICard
